import { Badge, Box, Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { PricingInfo, PricingPeriodType } from 'utils/apiUtils/prices'
import { removeDecimals } from 'utils/removeDecimals'

type Props = {
  info: PricingInfo
  pricingPeriods: PricingInfo[]
}

const PriceInfo = ({ info, pricingPeriods }: Props): JSX.Element => {
  const { t } = useTranslation('common')

  const getPeriodName = (type: PricingPeriodType): string => {
    switch (type) {
      case PricingPeriodType.HOURLY:
        return t('pricingInfoDescription.hourly')
      case PricingPeriodType.DAILY:
        return t('pricingInfoDescription.daily')
      case PricingPeriodType.WEEKLY:
        return t('pricingInfoDescription.weekly')
      default:
        return ''
    }
  }

  const getExtraInfo = (type: PricingPeriodType): string => {
    if (type === PricingPeriodType.DAILY) {
      const hourly = pricingPeriods.find((s) => s.type === PricingPeriodType.HOURLY)
      if (hourly) {
        return t('pricingInfoDescription.dailyAppliance')
      }
    }

    if (type == PricingPeriodType.WEEKLY) {
      const daily = pricingPeriods.find((s) => s.type === PricingPeriodType.DAILY)
      if (daily) {
        return t('pricingInfoDescription.weeklyAppliance')
      }
    }

    return ''
  }

  return (
    <>
      <Flex direction="row" justifyContent="space-between" alignItems="center" marginBottom={5}>
        <Flex direction="column">
          <Text as="span" fontWeight="semibold" lineHeight="normal">
            {getPeriodName(info.type)}
          </Text>

          <Text as="span" fontSize="sm" lineHeight="normal">
            {getExtraInfo(info.type)}
          </Text>
        </Flex>

        <Box textAlign="right">
          <Flex direction="row" gap={2} justifyContent="space-between" alignItems="center" marginBottom="10px">
            {info.possiblePrice && (
              <Text
                as="span"
                color="gray.a500"
                fontSize="md"
                fontWeight="normal"
                textDecoration="line-through"
                lineHeight="normal"
              >
                {removeDecimals(info.possiblePrice)} {t('nok')}
              </Text>
            )}

            <Text as="span" fontWeight="bold" fontSize={{ base: 'md', lg: 'xl' }} lineHeight="normal">
              {removeDecimals(info.price)} {t('nok')}
            </Text>
          </Flex>

          {info.profitPercentage && (
            <Badge colorPalette="green" size="lg" padding={2} marginBottom={2}>
              {t('pricingInfoDescription.youSave')} {info.profitPercentage}%
            </Badge>
          )}
        </Box>
      </Flex>
    </>
  )
}

export default PriceInfo
